import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import queryClient from '../../utils/queryClient';
import PicAPI, {
  type AlbumAssocPayload,
  type AlbumAssocResponse,
  type AlbumCreatePayload,
  type AlbumImportPayload,
  type AlbumImportPlan,
} from '../fetch/picApi';
import { useTranslation } from 'react-i18next';
import notify from '../../utils/notify';
import { orShopNotOpen } from '../../utils/shopNotOpen';
import userApi from '../fetch/userApi';
import type { ApiError } from '../fetch/apiClient';
import type { Album, Media, Uuid } from '../types';

export const useAlbumAssoc = () =>
  useMutation<AlbumAssocResponse, ApiError, AlbumAssocPayload>({
    mutationFn: PicAPI.albumAssoc,
    onSuccess: () => {
      void userApi.refreshToken();
      queryClient.invalidateQueries({ queryKey: ['albumList'] });
    },
    // Opening an album mints a guest session, which a shop that is not open refuses.
    onError: (error) => {
      notify('notify.error', orShopNotOpen(error, 'notify.albums.album_assoc_failed'), { type: 'warning' });
    },
  });

export const useCloseAlbum = () => {
  const client = useQueryClient();
  return useMutation<unknown, ApiError, Uuid>({
    mutationFn: PicAPI.closeAlbum,
    onSuccess: (_data, albumId) => {
      client.removeQueries({ queryKey: ['album', albumId] });
      void client.invalidateQueries({ queryKey: ['albumList'] });
      void client.invalidateQueries({ queryKey: ['albumAssociatedIds'] });
      notify('notify.success', 'notify.albums.album_closed');
    },
    onError: () => {
      notify('notify.error', 'notify.albums.album_close_failed', { type: 'danger' });
    },
  });
};

// How many ms before the server-declared pics URL expiry we want fresh data.
// Buys a margin for the request roundtrip plus minor clock skew so the <img>
// srcs are swapped while the previous batch is still valid.
const PICS_URL_REFRESH_AHEAD_MS = 5_000;

const msUntilPicsRefresh = (expiresAt: string | undefined): number | null => {
  if (!expiresAt) return null;
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return null;
  return parsed - Date.now() - PICS_URL_REFRESH_AHEAD_MS;
};

/**
 * staleTime for a list of album cards: fresh until just before the earliest
 * cover thumbnail URL expires (same reasoning as useAlbum below), 30 s when no
 * card has a cover.
 */
export const albumCardsStaleTime = (query: { state: { data?: Album[] } }): number => {
  const times = (query.state.data ?? [])
    .map((a) => msUntilPicsRefresh(a.cover_url_expires_at))
    .filter((ms): ms is number => ms !== null);
  return times.length === 0 ? 30_000 : Math.max(0, Math.min(...times));
};

export const useAlbumList = (enabled = true) => {
  // Name-sorted by the server in the UI language: the key carries it.
  const { i18n } = useTranslation();
  return useQuery<Album[]>({
    queryKey: ['albumList', i18n.language],
    queryFn: PicAPI.getAlbumList,
    enabled,
    staleTime: albumCardsStaleTime,
  });
};

export const useAlbum = (albumId: Uuid) =>
  useQuery<Album>({
    queryKey: ['album', albumId],
    queryFn: () => PicAPI.getAlbum(albumId),
    // Stay fresh until just before the server-declared expiry, then become
    // stale so the next observer mount (e.g. navigating back to the album)
    // triggers a refetch via refetchOnMount. No timer-driven refetch — images
    // already in the DOM keep working past expiry; only re-renders that issue
    // new <img> requests need fresh URLs.
    staleTime: (query) => {
      const ms = msUntilPicsRefresh(query.state.data?.pics_url_expires_at);
      if (ms === null) return 30_000;
      return Math.max(0, ms);
    },
  });

// Until pic#show answers, the pic's entry in the cached album list stands in
// (same media JSON, minus the restriction tags), so stepping to the next pic
// shows it at once instead of a page-wide loader — unless the album's signed
// URLs are about to expire. `isPlaceholderData` tells the page its tags aren't
// known yet.
export const usePic = (albumId: Uuid, picId: Uuid) => {
  const client = useQueryClient();
  return useQuery<Media>({
    queryKey: ['pic', albumId, picId],
    queryFn: () => PicAPI.getPic(albumId, picId),
    placeholderData: () => {
      const album = client.getQueryData<Album>(['album', albumId]);
      if ((msUntilPicsRefresh(album?.pics_url_expires_at) ?? 0) <= 0) return undefined;
      return album?.pics?.find((p) => p.id === picId);
    },
  });
};

export const useAllAlbums = () => {
  const { i18n } = useTranslation();
  return useQuery<Album[]>({
    queryKey: ['adminAlbumList', i18n.language],
    queryFn: PicAPI.getAllAlbums,
  });
};

// Optimistic reorder of an album's DIRECT pics. The drag UI already shows the
// new order, so we patch the ['album', albumId] cache up front, fire the PATCH,
// and revert + notify on failure. We invalidate on settle so the next fetch
// reconciles with the server (and refreshes presigned URLs).
export const useReorderAlbumPics = (albumId: Uuid) => {
  const qc = useQueryClient();
  return useMutation<Record<string, never>, ApiError, Uuid[], { previous?: Album }>({
    mutationFn: (mediaIds) => PicAPI.reorderAlbumPics(albumId, mediaIds),
    onMutate: async (mediaIds) => {
      await qc.cancelQueries({ queryKey: ['album', albumId] });
      const previous = qc.getQueryData<Album>(['album', albumId]);
      if (previous?.pics) {
        const orderIndex = new Map(mediaIds.map((id, i) => [id, i]));
        // Reorder direct pics by the new media_ids order; inherited pics (and any
        // direct pic not in the list) keep their relative position at the end.
        const reordered = [...previous.pics].sort((a, b) => {
          const ai = orderIndex.get(a.id);
          const bi = orderIndex.get(b.id);
          if (ai === undefined && bi === undefined) return 0;
          if (ai === undefined) return 1;
          if (bi === undefined) return -1;
          return ai - bi;
        });
        qc.setQueryData<Album>(['album', albumId], { ...previous, pics: reordered });
      }
      return { previous };
    },
    onError: (_err, _mediaIds, context) => {
      if (context?.previous) {
        qc.setQueryData(['album', albumId], context.previous);
      }
      notify('notify.error', 'notify.albums.reorder_failed', { type: 'warning' });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['album', albumId] });
    },
  });
};

// After creating album(s), the album-tree badges read from separate queries
// (priced album ids + effective products), not just the album list. Refresh all
// three so a freshly-created album — root albums get default products seeded —
// doesn't show a stale "no products" indicator until a page reload.
const invalidateAlbumTreeIndicators = () => {
  queryClient.invalidateQueries({ queryKey: ['adminAlbumList'] });
  queryClient.invalidateQueries({ queryKey: ['albumIdsWithPrices'] });
  queryClient.invalidateQueries({ queryKey: ['albumProductsEffective'] });
};

export const useCreateAlbum = () =>
  useMutation<Album, ApiError, AlbumCreatePayload>({
    mutationFn: PicAPI.createAlbum,
    onSuccess: invalidateAlbumTreeIndicators,
  });

export const useAlbumImportPreview = () =>
  useMutation<AlbumImportPlan, ApiError, AlbumImportPayload>({
    mutationFn: PicAPI.importPreview,
  });

export const useAlbumImport = () =>
  useMutation<AlbumImportPlan, ApiError, AlbumImportPayload>({
    mutationFn: PicAPI.importAlbums,
    onSuccess: invalidateAlbumTreeIndicators,
  });

export const useAlbums = (albumIds: Uuid[] | null | undefined) =>
  useQuery<Record<Uuid, Album>>({
    queryKey: ['albums', albumIds?.slice().sort()],
    queryFn: async () => {
      if (!albumIds || albumIds.length === 0) return {};
      const albumPromises = albumIds.map((id) => PicAPI.getAlbum(id));
      const albums = await Promise.all(albumPromises);
      return albums.reduce<Record<Uuid, Album>>((acc, album) => {
        acc[album.id] = album;
        return acc;
      }, {});
    },
    enabled: !!albumIds && albumIds.length > 0,
  });

